import { Children, Fragment, isValidElement, type ReactNode } from 'react';
import { ChevronRight, Info } from 'lucide-react';
import { cn } from '../../lib/utils';
import { useDocumentTitle } from '../../hooks/useDocumentTitle';
import { InlineDocsHint, PageTitle, PanelTitle, PageDescription } from './Typography';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '../ui/collapsible';
import { DescriptionHint } from '../shared/DescriptionHint';
import { useIntegrationEmbedded } from './IntegrationEmbed';
import { useRegisterPageChrome } from './page-chrome';

const maxWidthClasses = {
  '3xl': 'max-w-3xl',
  '4xl': 'max-w-4xl',
  '5xl': 'max-w-5xl',
  full: '',
} as const;

interface PageLayoutProps {
  children: ReactNode;
  maxWidth?: keyof typeof maxWidthClasses;
  className?: string;
}

export function PageLayout({ children, maxWidth = 'full', className }: PageLayoutProps) {
  return (
    <div className="flex h-full flex-col overflow-hidden">
      <div
        className="flex w-full flex-1 flex-col overflow-y-auto"
        tabIndex={0}
        role="region"
        aria-label="Page content"
      >
        <div
          data-testid="page-layout-body"
          className={cn(
            'flex w-full flex-1 flex-col gap-6 px-4 pb-4 pt-3 md:p-6',
            pageBodyUnderMobileChrome,
            maxWidth !== 'full' && `mx-auto ${maxWidthClasses[maxWidth]}`,
            className,
          )}
        >
          {children}
        </div>
      </div>
    </div>
  );
}

/**
 * Ancestor marker for takeover mobile chrome (`TakeoverAside`). Titles under
 * this node hide below `md` so the hamburger header is the only label.
 */
export const hideTitleUnderMobileChrome = '[[data-mobile-chrome-title]_&]:max-md:hidden';

/** Tighter page padding once takeover chrome already names the page. */
export const pageBodyUnderMobileChrome =
  '[[data-mobile-chrome-title]_&]:max-md:gap-4 [[data-mobile-chrome-title]_&]:max-md:pt-2';

export interface PageHeaderProps {
  icon?: ReactNode;
  /**
   * `inline` (default) renders the icon next to the title text.
   * `box` wraps the icon in a responsive rounded card — use for config and
   * resource list pages so the icon reads as the page's brand mark.
   */
  iconVariant?: 'inline' | 'box';
  /**
   * Page / section heading. Omit when the surrounding chrome already names
   * the section (e.g. a `HubLayout` rail) — keep `description`, `docs`, and
   * `actions` so the intro stays useful without repeating the label.
   */
  title?: string;
  description?: string;
  actions?: ReactNode;
  /**
   * Optional docs slug forwarded to the title (or beside the description when
   * `title` is omitted). Supports the `slug#anchor` form so headers can
   * deep-link into a specific section.
   */
  docs?: string;
  /**
   * Optional override for `document.title`. Defaults to `title`. Pass
   * `null` to skip setting the title (e.g. when an inner view owns it).
   * When `title` is omitted, defaults to `null`.
   */
  documentTitle?: string | null;
  /**
   * Optional inline badge rendered beside the title — use for maturity markers
   * (alpha/beta) or other inline status indicators that sit at the heading level.
   * Only applied for `iconVariant="inline"`.
   */
  badge?: ReactNode;
  /**
   * Optional collapsible "How to use" section rendered below the title row.
   * Pair the label (collapsed state) with the body content (expanded state).
   */
  howToUse?: {
    label: string;
    children: ReactNode;
  };
  /**
   * When true, hide the icon, title, and docs/info affordances under a
   * takeover mobile chrome bar (`data-mobile-chrome-title` ancestor) so the
   * `h-11` header is the only label. Nested detail pages pass this after
   * registering those bits with the chrome slot.
   */
  titleInMobileChrome?: boolean;
}

function resolveDocsNode(docs: PageHeaderProps['docs']) {
  if (docs === undefined) return null;
  if (typeof docs === 'string') {
    return docs.trim().length > 0 ? <InlineDocsHint slug={docs} /> : null;
  }
  return docs;
}

/** Docs hint + description popover, unwrapped so takeover chrome can reuse it. */
export function PageHeaderAffordances({
  docs,
  description,
}: {
  docs?: PageHeaderProps['docs'];
  description?: string;
}) {
  const docsNode = resolveDocsNode(docs);

  if (!docsNode && !description) {
    return null;
  }

  return (
    <div className="flex shrink-0 items-center gap-0.5" data-testid="page-header-title-affordances">
      {docsNode}
      {description ? (
        <DescriptionHint ariaLabel="About this page">{description}</DescriptionHint>
      ) : null}
    </div>
  );
}

/** Mobile-only title affordances — docs + description hint grouped after the title. */
function PageHeaderMobileTitleAffordances({
  docs,
  description,
  hideUnderChrome,
}: {
  docs?: PageHeaderProps['docs'];
  description?: string;
  hideUnderChrome?: boolean;
}) {
  const docsNode = resolveDocsNode(docs);
  if (!docsNode && !description) {
    return null;
  }

  return (
    <div className={cn('sm:hidden', hideUnderChrome && hideTitleUnderMobileChrome)}>
      <PageHeaderAffordances docs={docs} description={description} />
    </div>
  );
}

function countActionItems(actions: ReactNode): number {
  const items = Children.toArray(actions);
  if (items.length !== 1) return items.length;

  const [item] = items;
  if (
    isValidElement<{ children?: ReactNode }>(item) &&
    (item.type === Fragment || typeof item.type === 'string')
  ) {
    return Children.count(item.props.children);
  }

  return 1;
}

function HowToUseCollapsible({
  howToUse,
  variant = 'panel',
}: {
  howToUse: { label: string; children: ReactNode };
  variant?: 'panel' | 'quiet';
}) {
  if (variant === 'quiet') {
    return (
      <Collapsible className="group/collapse">
        <CollapsibleTrigger className="inline-flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground">
          <ChevronRight className="size-3.5 transition-transform group-data-[state=open]/collapse:rotate-90" />
          {howToUse.label}
        </CollapsibleTrigger>
        <CollapsibleContent>
          <div className="mt-2 space-y-3 text-sm text-muted-foreground">{howToUse.children}</div>
        </CollapsibleContent>
      </Collapsible>
    );
  }

  return (
    <Collapsible className="group/collapse rounded-lg border border-border">
      <CollapsibleTrigger className="flex w-full items-center gap-2 rounded-lg bg-muted/40 px-3 py-2 text-left text-sm transition-colors hover:bg-muted/70 group-data-[state=open]/collapse:rounded-b-none sm:px-4 sm:py-2.5">
        <ChevronRight className="h-3.5 w-3.5 shrink-0 text-muted-foreground transition-transform group-data-[state=open]/collapse:rotate-90" />
        <Info className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />
        <span className="font-medium text-muted-foreground">{howToUse.label}</span>
      </CollapsibleTrigger>
      <CollapsibleContent>
        <div className="space-y-3 border-t border-border bg-muted/20 px-3 py-3 text-sm text-muted-foreground sm:px-4">
          {howToUse.children}
        </div>
      </CollapsibleContent>
    </Collapsible>
  );
}

function EmbeddedPageHeader({
  actions,
  docs,
  howToUse,
}: {
  actions?: ReactNode;
  docs?: PageHeaderProps['docs'];
  howToUse?: PageHeaderProps['howToUse'];
}) {
  const docsNode = resolveDocsNode(docs);
  if (!actions && !howToUse && !docsNode) return null;

  return (
    <div className="flex flex-col gap-3">
      {(actions || docsNode) && (
        <div className="flex flex-wrap items-center justify-end gap-2">
          {docsNode}
          {actions}
        </div>
      )}
      {howToUse ? <HowToUseCollapsible howToUse={howToUse} variant="quiet" /> : null}
    </div>
  );
}

const multiActionMobileGrid = 'max-sm:grid max-sm:w-full max-sm:grid-cols-2 [&>*]:max-sm:w-full';

function BoxedPageHeader({
  icon,
  title,
  description,
  actions,
  docs,
  howToUse,
  titleInMobileChrome,
}: {
  icon: ReactNode;
  title: string;
  description?: string;
  actions?: ReactNode;
  docs?: string;
  howToUse?: PageHeaderProps['howToUse'];
  titleInMobileChrome?: boolean;
}) {
  const spansTitleAndDescription = Boolean(description);
  const hasSingleAction = countActionItems(actions) === 1;

  const collapseUnderChrome = Boolean(titleInMobileChrome) && !actions && !howToUse;

  return (
    <div
      className={cn(
        'flex flex-col gap-3 sm:gap-4',
        collapseUnderChrome && hideTitleUnderMobileChrome,
      )}
    >
      <div
        className={cn(
          'grid items-center gap-x-3 sm:gap-x-4',
          actions
            ? 'gap-y-2 sm:grid-cols-[auto_minmax(0,1fr)_auto] sm:gap-y-0.5'
            : 'grid-cols-[auto_minmax(0,1fr)] gap-y-0.5',
          actions &&
            (hasSingleAction
              ? 'grid-cols-[auto_minmax(0,1fr)_auto]'
              : 'grid-cols-[auto_minmax(0,1fr)]'),
        )}
      >
        <div
          className={cn(
            'flex h-9 w-9 shrink-0 items-center justify-center rounded-xl border border-border/60 bg-muted/80 shadow-sm sm:h-12 sm:w-12 sm:rounded-2xl [&_svg]:h-4 [&_svg]:w-4 sm:[&_svg]:h-5 sm:[&_svg]:w-5',
            hideTitleUnderMobileChrome,
            spansTitleAndDescription && 'sm:row-span-2',
          )}
        >
          {icon}
        </div>
        <div className="col-start-2 flex min-w-0 items-center gap-1.5">
          <PanelTitle
            docs={docs}
            className={cn(
              'min-w-0 max-sm:flex-1 [&_[data-testid=panel-title-docs-slot]]:max-sm:hidden',
              hideTitleUnderMobileChrome,
            )}
          >
            {title}
          </PanelTitle>
          <PageHeaderMobileTitleAffordances
            docs={docs}
            description={description}
            hideUnderChrome={titleInMobileChrome}
          />
        </div>
        {actions && (
          <div
            className={cn(
              'flex flex-wrap items-center gap-2 sm:col-span-1 sm:col-start-3 sm:row-start-1 sm:shrink-0 sm:self-center',
              // A single compact action fits beside the title on narrow headers.
              // Multi-action groups keep their own row so they do not crush the title;
              // trail them to the right so the primary action sits at the edge.
              hasSingleAction
                ? cn(
                    'col-start-3 row-start-1 shrink-0',
                    // Takeover chrome hides the icon + title below `md`, which
                    // would otherwise leave a lone action trailing in column 3.
                    titleInMobileChrome &&
                      '[[data-mobile-chrome-title]_&]:max-md:col-span-full [[data-mobile-chrome-title]_&]:max-md:col-start-1 [[data-mobile-chrome-title]_&]:max-md:justify-start',
                  )
                : cn('col-span-2 col-start-1 row-start-2 justify-end', multiActionMobileGrid),
              spansTitleAndDescription && 'sm:row-span-2',
            )}
            data-testid="page-header-actions"
          >
            {actions}
          </div>
        )}
        {description ? (
          <PageDescription className="col-start-2 mt-0.5 max-sm:hidden">
            {description}
          </PageDescription>
        ) : null}
      </div>

      {/* Content-top slot for plugin-contributed banners (e.g. guided tutorials). */}
      <div data-page-banner-region="" className="shrink-0 empty:hidden" />

      {howToUse ? <HowToUseCollapsible howToUse={howToUse} /> : null}
    </div>
  );
}

/**
 * Hub-embedded intro: description + docs (+ optional actions / howToUse)
 * without a second heading. The hub title and section rail already name the
 * section; repeating it at `PageTitle` weight is visually heavy.
 */
function TitlelessPageHeader({
  description,
  actions,
  docs,
  howToUse,
  titleInMobileChrome,
}: {
  description?: string;
  actions?: ReactNode;
  docs?: string;
  howToUse?: PageHeaderProps['howToUse'];
  titleInMobileChrome?: boolean;
}) {
  const docsNode = resolveDocsNode(docs);
  const collapseUnderChrome = Boolean(titleInMobileChrome) && !actions && !howToUse;

  return (
    <div
      className={cn(
        'flex flex-col gap-2 sm:gap-4',
        collapseUnderChrome && hideTitleUnderMobileChrome,
      )}
      data-testid="page-header-titleless"
    >
      <div className="flex flex-wrap items-start justify-between gap-3 sm:flex-nowrap sm:gap-4">
        <div
          className={cn(
            'flex min-w-0 items-start gap-1.5',
            titleInMobileChrome && hideTitleUnderMobileChrome,
          )}
        >
          {description ? <PageDescription className="mt-0">{description}</PageDescription> : null}
          {docsNode ? <span className="mt-0.5 shrink-0">{docsNode}</span> : null}
        </div>
        {actions && <div className="flex shrink-0 items-center gap-2">{actions}</div>}
      </div>
      <div data-page-banner-region="" className="shrink-0 empty:hidden" />
      {howToUse ? <HowToUseCollapsible howToUse={howToUse} /> : null}
    </div>
  );
}

export function PageHeader({
  icon,
  iconVariant = 'inline',
  title,
  description,
  actions,
  docs,
  documentTitle,
  badge,
  howToUse,
  titleInMobileChrome,
}: PageHeaderProps) {
  const embedded = useIntegrationEmbedded();
  const resolvedDocumentTitle =
    documentTitle === undefined ? (title === undefined ? null : title) : documentTitle;
  useDocumentTitle(embedded ? null : resolvedDocumentTitle);

  // Plugins import this header, not web's wrapper. Register docs / description
  // so takeover mobile chrome can hoist the info icon. Leave `title` unset so
  // the shell nav label (e.g. "Model Costs") is not replaced by a shorter
  // in-page heading ("Costs"). Host pages still publish title from web.
  // Skip chrome while embedded so a Connection-card header does not hoist into
  // the takeover bar. Call the hook unconditionally (rules of hooks).
  const chromeActive = useRegisterPageChrome(embedded ? {} : { docs, description });
  const hoistToChrome = titleInMobileChrome || chromeActive;

  if (embedded) {
    return <EmbeddedPageHeader actions={actions} docs={docs} howToUse={howToUse} />;
  }

  if (!title) {
    return (
      <TitlelessPageHeader
        description={description}
        actions={actions}
        docs={docs}
        howToUse={howToUse}
        titleInMobileChrome={hoistToChrome}
      />
    );
  }

  if (iconVariant === 'box' && icon) {
    return (
      <BoxedPageHeader
        icon={icon}
        title={title}
        description={description}
        actions={actions}
        docs={docs}
        howToUse={howToUse}
        titleInMobileChrome={hoistToChrome}
      />
    );
  }

  const hasSingleAction = countActionItems(actions) === 1;
  const collapseUnderChrome = hoistToChrome && !actions && !howToUse;

  return (
    <div
      className={cn(
        'flex flex-col gap-2 sm:gap-4',
        collapseUnderChrome && hideTitleUnderMobileChrome,
      )}
    >
      <div className="flex flex-wrap items-start justify-between gap-3 sm:flex-nowrap sm:gap-4">
        <div className="min-w-0">
          <div className="flex items-center gap-1.5">
            <PageTitle
              icon={icon}
              docs={docs}
              className={cn(
                'min-w-0 max-sm:flex-1 [&_[data-testid=page-title-docs-slot]]:max-sm:hidden',
                hideTitleUnderMobileChrome,
              )}
            >
              {title}
            </PageTitle>
            {badge && (
              <span className="inline-flex shrink-0 items-center self-center">{badge}</span>
            )}
            <PageHeaderMobileTitleAffordances
              docs={docs}
              description={description}
              hideUnderChrome={hoistToChrome}
            />
          </div>
          {description && (
            <PageDescription className="max-sm:hidden">{description}</PageDescription>
          )}
        </div>
        {actions && (
          <div
            className={cn(
              'flex shrink-0 items-center gap-2',
              !hasSingleAction && multiActionMobileGrid,
            )}
            data-testid="page-header-actions"
          >
            {actions}
          </div>
        )}
      </div>
      <div data-page-banner-region="" className="shrink-0 empty:hidden" />
      {howToUse ? <HowToUseCollapsible howToUse={howToUse} /> : null}
    </div>
  );
}
